import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import type { Observable } from 'rxjs';
import { BehaviorSubject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private readonly translate = inject(TranslateService);

  private readonly defaultLang = 'en';
  private readonly currentLang$ = new BehaviorSubject<string>(this.defaultLang);

  constructor() {
    if (this.isBrowser) {
      const STORED_LANG = localStorage.getItem('lang') || this.defaultLang;
      this.setLanguage(STORED_LANG);
    } else {
      this.translate.use(this.defaultLang);
    }
  }

  setLanguage(lang: string): void {
    this.translate.use(lang).subscribe(() => {
      if (!this.isBrowser) {
        return;
      }

      localStorage.setItem('lang', lang);
      this.currentLang$.next(lang);
      document.documentElement.lang = lang;
      document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    });
  }

  getCurrentLang(): string {
    return this.translate.currentLang || this.defaultLang;
  }

  getCurrentLang$(): Observable<string> {
    return this.currentLang$.asObservable();
  }

  toggleLanguage(): void {
    const NEW_LANG = this.getCurrentLang() === 'en' ? 'ar' : 'en';
    this.setLanguage(NEW_LANG);
  }
}
